<template>
  <component :is="section ? 'div' : 'v-card'" :class="{ 'mb-4': !section }">
    <!-- Dans la fiche de la borne : une section comme les autres (titre, libellé : valeur) -->
    <template v-if="section">
      <headline class="mt-10">{{ $t('onboarding.profile-change.current') }}</headline>
      <div class="grid">
        <v-row align="center">
          <v-col class="label" sm="3">{{ $t('onboarding.profile-change.profile') }} :</v-col>
          <v-col>
            <div v-if="access" class="d-flex align-center flex-wrap" style="gap: 8px">
              <span>{{ access.profileName || '-' }}</span>
              <v-chip x-small outlined color="primary">{{ $t(`onboarding.equipment-profile.usage.${access.usage}`) }}</v-chip>
              <span class="text-caption text--secondary">{{ $t(`onboarding.equipment-profile.publication.${access.usage}`) }}</span>
            </div>
            <span v-else class="text--secondary">{{ $t('onboarding.profile-change.none') }}</span>
          </v-col>
          <v-col v-if="canManage" cols="auto">
            <v-btn small outlined color="primary" @click="open">
              <v-icon left small>mdi-swap-horizontal</v-icon>{{ $t('onboarding.profile-change.button') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </template>
    <v-card-text v-else class="d-flex align-center flex-wrap" style="gap: 10px">
      <v-icon>mdi-tune-variant</v-icon>
      <div class="flex-grow-1">
        <div class="text-caption text--secondary">{{ $t('onboarding.profile-change.current') }}</div>
        <div v-if="access" class="d-flex align-center flex-wrap" style="gap: 8px">
          <span class="font-weight-medium">{{ access.profileName || '-' }}</span>
          <v-chip x-small outlined color="primary">{{ $t(`onboarding.equipment-profile.usage.${access.usage}`) }}</v-chip>
          <span class="text-caption text--secondary">{{ $t(`onboarding.equipment-profile.publication.${access.usage}`) }}</span>
        </div>
        <div v-else class="text--secondary">{{ $t('onboarding.profile-change.none') }}</div>
      </div>
      <v-btn v-if="canManage" small outlined color="primary" @click="open">
        <v-icon left small>mdi-swap-horizontal</v-icon>{{ $t('onboarding.profile-change.button') }}
      </v-btn>
    </v-card-text>

    <!-- L'assistant : le profil, ce qui change, confirmer -->
    <v-dialog v-model="wizard.open" max-width="720" scrollable>
      <v-card>
        <v-card-title>{{ $t('onboarding.profile-change.title') }}</v-card-title>
        <v-card-text>
          <p class="text-caption text--secondary">{{ $t('onboarding.profile-change.explained') }}</p>
          <v-select
              v-model="wizard.profileId" :items="choices" item-value="id" :item-text="profileText"
              :label="$t('onboarding.charging-policy.profile')" :loading="loadingProfiles"
              outlined dense @change="preview"
          />
          <v-alert v-if="wizard.error" type="error" dense outlined>{{ wizard.error }}</v-alert>
          <v-progress-linear v-if="wizard.loading" indeterminate class="mb-3" />

          <v-simple-table v-if="wizard.preview" dense class="mb-2">
            <thead>
              <tr>
                <th />
                <th>{{ $t('onboarding.profile-change.today') }}</th>
                <th>{{ $t('onboarding.profile-change.after') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>{{ $t('onboarding.charging-policy.profile') }}</th>
                <td>{{ wizard.preview.current.profileName || '-' }}</td>
                <td><strong>{{ wizard.preview.next.profileName }}</strong></td>
              </tr>
              <tr>
                <th>{{ $t('onboarding.equipment-profile.usage-title') }}</th>
                <td>{{ usageText(wizard.preview.current.usage) }}</td>
                <td><strong>{{ usageText(wizard.preview.next.usage) }}</strong></td>
              </tr>
              <tr>
                <th>{{ $t('onboarding.equipment-profile.access-title') }}</th>
                <td>{{ accessText(wizard.preview.current.access) }}</td>
                <td><strong>{{ accessText(wizard.preview.next.access) }}</strong></td>
              </tr>
              <tr>
                <th>{{ $t('onboarding.equipment-profile.licence-title') }}</th>
                <td>{{ licencesText(wizard.preview.current.licences) }}</td>
                <td><strong>{{ wizard.preview.licence.keep ? $t('onboarding.profile-change.licence-kept') : licencesText([wizard.preview.next.licence]) }}</strong></td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-alert v-if="wizard.preview && !wizard.preview.licence.keep" type="warning" dense text class="mb-0">
            {{ $t('onboarding.profile-change.licence-replaced') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="wizard.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!wizard.preview" :loading="wizard.running" @click="confirm">{{ $t('onboarding.profile-change.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </component>
</template>

<script>
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import MemberGroupRepository from '@repository/MemberGroupRepository'

/**
 * Le profil d'équipement d'une borne posée, et l'assistant pour en changer : choisir un profil de borne de son
 * organisation, du même côté (site ou domicile), voir ce qui change — usage donc publication, accès, licence —, puis
 * confirmer. Le serveur reste juge (et refuse, par exemple, de mélanger bornes publiques et privées à un emplacement).
 */
export default {
  name: 'ChargingPointProfileCard',

  props: {
    chargingPoint: { type: Object, required: true },
    /** Une section de la fiche de la borne, dans son style, au lieu d'une carte. */
    section: { type: Boolean, default: false },
  },

  data() {
    return {
      profiles: [],
      groups: [],
      subscriptions: [],
      loadingProfiles: false,
      /** Domicile ou site : le serveur le dit (la borne ne le porte pas dans sa fiche). */
      atHome: null,
      wizard: { open: false, profileId: null, preview: null, loading: false, running: false, error: null },
    }
  },

  computed: {
    access() {
      return this.chargingPoint.access || null
    },

    organizationId() {
      return this.chargingPoint.organization?.id || null
    },

    canManage() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE') && !!this.organizationId
    },

    /** Les profils de borne actifs, du même côté que la borne, sauf celui qu'elle a déjà. */
    choices() {
      return this.profiles.filter(p => p.kind === 'charging-point' && !p.archived && (p.usage === 'home') === this.atHome && p.id !== this.access?.profileId)
    },
  },

  methods: {
    async open() {
      this.wizard = { open: true, profileId: null, preview: null, loading: false, running: false, error: null }
      this.loadingProfiles = true
      const [today, profiles, groups, subscriptions] = await Promise.all([
        EquipmentProfileRepository.previewChange(this.chargingPoint.id, null).catch(() => null),
        EquipmentProfileRepository.list(this.organizationId).catch(() => []),
        MemberGroupRepository.search({ organizationId: this.organizationId, offset: 0, limit: 200 }).catch(() => ({ items: [] })),
        CommissioningPolicyRepository.licenceSubscriptions(this.organizationId).catch(() => []),
      ])
      this.atHome = today ? today.atHome : null
      this.profiles = profiles
      this.groups = groups.items || []
      this.subscriptions = subscriptions
      this.loadingProfiles = false
    },

    async preview() {
      this.wizard.preview = null
      this.wizard.error = null
      if (!this.wizard.profileId) return
      this.wizard.loading = true
      try {
        this.wizard.preview = await EquipmentProfileRepository.previewChange(this.chargingPoint.id, this.wizard.profileId)
      } catch (e) {
        this.wizard.error = this.messageOf(e)
      } finally {
        this.wizard.loading = false
      }
    },

    async confirm() {
      this.wizard.running = true
      this.wizard.error = null
      try {
        await EquipmentProfileRepository.change(this.chargingPoint.id, this.wizard.profileId)
        this.wizard.open = false
        this.notifySuccess('onboarding.profile-change.done')
        this.$emit('changed')
      } catch (e) {
        this.wizard.error = this.messageOf(e)
      } finally {
        this.wizard.running = false
      }
    },

    profileText(profile) {
      return `${profile.name} · ${this.$t(`onboarding.equipment-profile.usage.${profile.usage}`)}`
    },

    usageText(usage) {
      return usage ? `${this.$t(`onboarding.equipment-profile.usage.${usage}`)} · ${this.$t(`onboarding.equipment-profile.publication.${usage}`)}` : '-'
    },

    accessText(grants) {
      if (!grants || !grants.length) return '-'
      return grants.map(g => `${this.groups.find(x => x.id === g.groupId)?.name || '-'} (${this.$t(g.free ? 'onboarding.equipment-profile.access-free' : 'onboarding.equipment-profile.access-paid').toLowerCase()})`).join(', ')
    },

    /** « Abonnement · formule » de chaque licence. */
    licencesText(licences) {
      if (!licences || !licences.length) return '-'
      return licences.map(l => {
        const subscription = this.subscriptions.find(s => s.id === l.subscriptionId)
        const formula = subscription?.formulas?.find(f => f.id === l.formulaId)
        return [subscription?.name || l.subscriptionId, formula?.name].filter(Boolean).join(' · ')
      }).join(', ')
    },

    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      if (key && this.$te(key)) return this.$t(key, data.parameters || {})
      return data?.message || this.$t('error-occurred')
    },
  },
}
</script>
